/**
 * Super-admin Backup page.
 *
 * - GET    /api/admin/backup/config   → current schedule + status + mongodump availability
 * - PUT    /api/admin/backup/config   → enabled / target_dir / schedule_time_utc / keep_last_n
 * - POST   /api/admin/backup/run-now  → fire an immediate dump (sync, returns run row)
 * - GET    /api/admin/backup/runs     → last 50 runs
 * - GET    /api/admin/backup/runs/{id}/download  → stream the archive.gz to the browser
 * - DELETE /api/admin/backup/runs/{id} → unlink + remove row
 */
import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Database, FloppyDisk, DownloadSimple, Trash, ArrowsClockwise,
  CheckCircle, Warning, Clock, HardDrives,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUS_CLS = {
  success: "border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10",
  failed: "border-[var(--vs-danger)]/40 text-[var(--vs-danger)] bg-[var(--vs-danger)]/10",
};
const STATUS_CLS_PENDING = "border-[var(--vs-warning)]/40 text-[var(--vs-warning)] bg-[var(--vs-warning)]/10";

function StatusPill({ status }) {
  const live = status === "success";
  const failed = status === "failed";
  const cls = STATUS_CLS[status] || STATUS_CLS_PENDING;
  return (
    <Badge variant="outline" className={cls}>
      {live && <CheckCircle size={11} className="mr-1" weight="duotone" />}
      {failed && <Warning size={11} className="mr-1" weight="duotone" />}
      {!live && !failed && <Clock size={11} className="mr-1" weight="duotone" />}
      {status || "—"}
    </Badge>
  );
}

function formatSize(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(2)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function Backup() {
  const [cfg, setCfg] = useState(null);
  const [runs, setRuns] = useState([]);
  const [form, setForm] = useState({
    enabled: false,
    target_dir: "",
    schedule_time_utc: "02:00",
    keep_last_n: 14,
  });
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const loadConfig = useCallback(async () => {
    const r = await api.get("/admin/backup/config");
    setCfg(r.data);
    setForm({
      enabled: !!r.data.enabled,
      target_dir: r.data.target_dir || "",
      schedule_time_utc: r.data.schedule_time_utc || "02:00",
      keep_last_n: r.data.keep_last_n || 14,
    });
  }, []);

  const loadRuns = useCallback(async () => {
    const r = await api.get("/admin/backup/runs");
    setRuns(r.data);
  }, []);

  useEffect(() => {
    loadConfig();
    loadRuns();
  }, [loadConfig, loadRuns]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/backup/config", form);
      toast.success("Backup configuration saved.");
      await loadConfig();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/backup/run-now");
      if (r.data.status === "success") {
        toast.success(`Backup complete — ${formatSize(r.data.size_bytes)}`);
      } else {
        toast.error(`Backup failed (exit ${r.data.exit_code || "?"})`);
      }
      await Promise.all([loadConfig(), loadRuns()]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setRunning(false);
    }
  };

  const download = async (runId) => {
    try {
      const r = await api.get(`/admin/backup/runs/${runId}/download`, { responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const fname = m ? m[1] : `vsms-backup-${runId}.archive.gz`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Download failed");
    }
  };

  const remove = async (runId) => {
    if (!window.confirm("Delete this backup archive permanently?")) return;
    try {
      await api.delete(`/admin/backup/runs/${runId}`);
      toast.success("Archive deleted.");
      await loadRuns();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    }
  };

  if (!cfg) {
    return (
      <DashboardLayout title="Data Backup" subtitle="Loading…">
        <div className="text-sm text-[var(--vs-text-secondary)]">Loading backup configuration…</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout
      title="Data Backup"
      subtitle="Scheduled MongoDB snapshots — encrypted-at-rest archive.gz dumps stored locally."
    >
      {/* Status banners */}
      <div className="grid md:grid-cols-3 gap-3 mb-6">
        <div className="vs-card p-4">
          <div className="text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)] mb-1">mongodump</div>
          <div className="flex items-center gap-2">
            {cfg.mongodump_available ? (
              <Badge variant="outline" className="border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10">
                <CheckCircle size={11} className="mr-1" weight="duotone" /> Available
              </Badge>
            ) : (
              <Badge variant="outline" className="border-[var(--vs-danger)]/40 text-[var(--vs-danger)] bg-[var(--vs-danger)]/10">
                <Warning size={11} className="mr-1" weight="duotone" /> Missing on PATH
              </Badge>
            )}
          </div>
        </div>
        <div className="vs-card p-4">
          <div className="text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)] mb-1">Target directory</div>
          <div className="font-mono text-xs flex items-center gap-1.5">
            <HardDrives size={12} /> {cfg.target_dir_resolved}
            {cfg.target_dir_writable ? (
              <Badge variant="outline" className="ml-1 border-[var(--vs-success)]/40 text-[var(--vs-success)] bg-[var(--vs-success)]/10">writable</Badge>
            ) : (
              <Badge variant="outline" className="ml-1 border-[var(--vs-danger)]/40 text-[var(--vs-danger)] bg-[var(--vs-danger)]/10">read-only</Badge>
            )}
          </div>
        </div>
        <div className="vs-card p-4">
          <div className="text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)] mb-1">Last run</div>
          <div className="flex items-center gap-2 text-xs font-mono">
            <StatusPill status={cfg.last_run_status} />
            {cfg.last_run_at ? new Date(cfg.last_run_at).toLocaleString() : "—"}
          </div>
        </div>
      </div>

      {/* Config form */}
      <div className="vs-card p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Database size={16} weight="duotone" />
          <h3 className="font-semibold text-base">Schedule</h3>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-3 bg-[var(--vs-bg-subtle)] rounded">
            <div>
              <Label htmlFor="bk-enabled" className="text-sm">Enable daily auto-backup</Label>
              <p className="text-xs text-[var(--vs-text-secondary)] mt-1">Fires once per day at the configured UTC time.</p>
            </div>
            <Switch
              id="bk-enabled"
              data-testid="bk-enabled"
              checked={form.enabled}
              onCheckedChange={(v) => setForm({ ...form, enabled: v })}
            />
          </div>
          <div>
            <Label htmlFor="bk-time">Daily schedule (UTC, HH:MM)</Label>
            <Input
              id="bk-time"
              data-testid="bk-time"
              value={form.schedule_time_utc}
              onChange={(e) => setForm({ ...form, schedule_time_utc: e.target.value })}
              className="font-mono"
              placeholder="02:00"
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="bk-target">Target directory (server-local)</Label>
            <Input
              id="bk-target"
              data-testid="bk-target"
              value={form.target_dir}
              onChange={(e) => setForm({ ...form, target_dir: e.target.value })}
              className="font-mono"
              placeholder="/var/backups/vsms"
            />
            <p className="text-xs text-[var(--vs-text-secondary)] mt-1">
              Archives are saved here as <code>vsms-&lt;timestamp&gt;.archive.gz</code>. Download to local drive from the Runs table below.
            </p>
          </div>
          <div>
            <Label htmlFor="bk-keep">Retention (keep last N)</Label>
            <Input
              id="bk-keep"
              data-testid="bk-keep"
              type="number"
              min="1"
              max="365"
              value={form.keep_last_n}
              onChange={(e) => setForm({ ...form, keep_last_n: Number(e.target.value) })}
              className="font-mono"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 mt-5">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="bk-save"
            className="bg-[var(--vs-primary)] hover:bg-[var(--vs-primary-hover)] text-[var(--vs-text)]"
          >
            <FloppyDisk size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save schedule"}
          </Button>
          <Button
            variant="outline"
            onClick={runNow}
            disabled={running}
            data-testid="bk-run-now"
          >
            <ArrowsClockwise size={14} className="mr-1.5" /> {running ? "Backing up…" : "Run backup now"}
          </Button>
        </div>
      </div>

      {/* Runs table */}
      <div className="vs-card overflow-x-auto">
        <div className="flex items-center justify-between p-4 border-b border-[var(--vs-border)]">
          <h3 className="font-semibold text-base">Backup history</h3>
          <Button variant="ghost" size="sm" onClick={loadRuns} data-testid="bk-refresh">
            <ArrowsClockwise size={12} className="mr-1" /> Refresh
          </Button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-[var(--vs-bg-subtle)]">
            <tr className="text-left text-[11px] uppercase font-mono tracking-widest text-[var(--vs-text-secondary)]">
              <th className="px-4 py-3">Started</th>
              <th className="px-4 py-3">Finished</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Size</th>
              <th className="px-4 py-3">Trigger</th>
              <th className="px-4 py-3">Archive</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="bk-runs-table">
            {runs.map((r) => (
              <tr key={r.id} data-testid={`bk-run-${r.id}`} className="border-t border-[var(--vs-border)]">
                <td className="px-4 py-3 font-mono text-xs">{new Date(r.started_at).toLocaleString()}</td>
                <td className="px-4 py-3 font-mono text-xs">{r.finished_at ? new Date(r.finished_at).toLocaleString() : "—"}</td>
                <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                <td className="px-4 py-3 font-mono text-xs">{formatSize(r.size_bytes)}</td>
                <td className="px-4 py-3 font-mono text-xs">{(r.triggered_by || "").split(":")[0]}</td>
                <td className="px-4 py-3 font-mono text-[10px] text-[var(--vs-text-secondary)] truncate max-w-[260px]" title={r.target_path}>
                  {r.target_path ? r.target_path.split("/").pop() : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  {r.status === "success" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => download(r.id)}
                      data-testid={`bk-download-${r.id}`}
                      className="mr-2"
                    >
                      <DownloadSimple size={12} className="mr-1" /> Download
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(r.id)}
                    data-testid={`bk-delete-${r.id}`}
                  >
                    <Trash size={12} />
                  </Button>
                </td>
              </tr>
            ))}
            {runs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-[var(--vs-text-secondary)]">
                  No backup runs yet — click <strong>Run backup now</strong> to create one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
}
